import { MouseEvent, useEffect, useRef, useState } from 'react'
import { Button, Card, Stack, Typography } from "@mui/material"
import { IconLike } from "../../../../icons/IconLike"
import { IconComments } from "../../../../icons/IconComments"
import { CardMediaStyle, TypDescription, TypTitle } from "./style"
import { useNavigate } from "react-router-dom"
import { routesProfile } from "../../../../consts"
import { IProjectsListItem } from '../../../../api/types'
import { resolveProjectCommentsTotal } from '../../../../api/types/projects'
import { formatNumber } from '../../../../common/FormatNumber'
import { profileApi } from '../../../../api/profile'
import modalEmitter from '../../../ModalComponent/modalEmitter'
import { ProjectLikesModalBody } from '../../../Modals'
import {
  useLikeProjectMutation,
  useSubscribeProjectMutation,
  useUnlikeProjectMutation,
  useUnsubscribeProjectMutation,
} from '../../../../api/projects'
import { toast } from 'react-toastify'
import { IconLikeFilled } from '../../../../icons/IconLikeFilled'
import { ProjectCoverImage } from '../../../common/ProjectCoverImage'
import { ProjectStatusChip } from '../../ProjectStatusChip'
import { ViewCounter } from '../../../ui-kit/ViewCounter'
import { useSettledToggleNetwork } from '../../../../hooks/useSettledToggleNetwork'

interface ProjectItemProps {
  project: IProjectsListItem
}

export const MobileCard = ({ project }: ProjectItemProps) => {
    const navigation = useNavigate();
    const { data: me } = profileApi.useGetProfileQuery()
    const isAuthor = me?.id !== undefined && me?.id === project.author_id;
    const [subscribeProject, { isLoading: isSubscribeLoading }] = useSubscribeProjectMutation()
    const [unsubscribeProject, { isLoading: isUnsubscribeLoading }] = useUnsubscribeProjectMutation()
    const [likeProject, likeState] = useLikeProjectMutation()
    const [unlikeProject, unlikeState] = useUnlikeProjectMutation()
    const [isFollowed, setIsFollowed] = useState(project.is_followed === true)
    const [subscribersCount, setSubscribersCount] = useState(Number(project.counters?.subscribers ?? 0))
    const [isLiked, setIsLiked] = useState(project.viewer?.is_liked === true)
    const [likesCount, setLikesCount] = useState(Number(project.counters.likes ?? 0))
    const subscribeSettle = useSettledToggleNetwork()
    const likeSettle = useSettledToggleNetwork()
    const subscribeBurstRef = useRef<boolean | null>(null)
    const likeBurstRef = useRef<{ value: boolean; count: number } | null>(null)

    useEffect(() => {
        setIsFollowed(project.is_followed === true)
        setSubscribersCount(Number(project.counters?.subscribers ?? 0))
    }, [project.id, project.is_followed, project.counters?.subscribers])

    useEffect(() => {
        setIsLiked(project.viewer?.is_liked === true)
        setLikesCount(Number(project.counters.likes ?? 0))
    }, [project.id, project.viewer?.is_liked, project.counters?.likes])

    const viewsCount = Number(project.counters?.views ?? 0)
    const membersCount = Number(project.counters?.members ?? 0)
    
    const navigateToProject = () => {
        navigation(`/${routesProfile.PROJECTS.path}/${project.slug}`)
    }
    const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        navigation(`/${routesProfile.PROJECTS.path}/${project.id}/edit`)
    };
    const handleStopNavigation = (e: any) => {
        e.preventDefault?.()
        e.stopPropagation?.()
    }

    const handleOpenProjectDiscussion = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        navigation(`/${routesProfile.PROJECTS.path}/${project.slug}?discussion=1`)
    }

    const handleToggleLike = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (likeState.isLoading || unlikeState.isLoading) return
        if (!me?.id || !project.viewer) {
            toast.info("Войдите, чтобы ставить лайки")
            return
        }
        if (!likeBurstRef.current) {
            likeBurstRef.current = { value: isLiked, count: likesCount }
        }
        const next = !isLiked
        setIsLiked(next)
        setLikesCount((n) => Math.max(0, n + (isLiked ? -1 : 1)))

        likeSettle.schedule({
            desired: next,
            commit: async (desired) => {
                likeBurstRef.current = null
                if (desired) {
                    await likeProject({ projectId: project.id, slug: project.slug }).unwrap()
                } else {
                    await unlikeProject({ projectId: project.id, slug: project.slug }).unwrap()
                }
            },
            onSkip: () => {
                likeBurstRef.current = null
            },
            onError: () => {
                const burst = likeBurstRef.current
                likeBurstRef.current = null
                if (burst) {
                    setIsLiked(burst.value)
                    setLikesCount(burst.count)
                }
                toast.error("Не удалось изменить лайк")
            },
        })
    }

    const handleOpenLikes = (e: any) => {
        e.preventDefault?.()
        e.stopPropagation?.()
        if (likesCount <= 0) return
        modalEmitter.emitOpenModal({
            dialog: {
                title: 'Лайки',
                body: <ProjectLikesModalBody projectId={project.id} />,
                modalToCentrPosition: true,
            },
        })
    }

    const handleToggleSubscribe = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        if (isSubscribeLoading || isUnsubscribeLoading) return
        if (subscribeBurstRef.current === null) {
            subscribeBurstRef.current = isFollowed
        }
        const next = !isFollowed
        setIsFollowed(next)
        setSubscribersCount((n) => Math.max(0, n + (isFollowed ? -1 : 1)))

        subscribeSettle.schedule({
            desired: next,
            commit: async (desired) => {
                subscribeBurstRef.current = null
                if (desired) {
                    await subscribeProject({ projectId: project.id, slug: project.slug }).unwrap()
                    toast.success("Вы подписались на проект")
                } else {
                    await unsubscribeProject({ projectId: project.id, slug: project.slug }).unwrap()
                    toast.success("Вы отписались от проекта")
                }
            },
            onSkip: () => {
                subscribeBurstRef.current = null
            },
            onError: (baseline) => {
                subscribeBurstRef.current = null
                setIsFollowed(baseline)
                setSubscribersCount(Number(project.counters?.subscribers ?? 0))
                toast.error("Не удалось изменить подписку")
            },
        })
    }

    return(
        <Card 
            variant="primaryAdaptiveCard" 
            onClick={navigateToProject}
            sx={{ overflow: 'visible', }}
        >
            <Stack 
                direction="row" 
                justifyContent="space-between"
                alignItems="center"
                padding="12px 0px 8px 0px"
                width="100%"
            >
                <Typography sx={TypTitle} variant="black-14-medium">
                    {project.name}
                </Typography>
                {isAuthor ? (
                    <Button variant="follow" sx={{ ml: 1 }} onClick={handleEdit}>Редактировать</Button>
                ) : (
                    <Button
                        variant="follow"
                        sx={{ ml: 1 }}
                        onClick={handleToggleSubscribe}
                        disabled={isSubscribeLoading || isUnsubscribeLoading}
                    >
                        {isFollowed ? "Отписаться" : "Подписаться"}
                    </Button>
                )}
            </Stack>
            <ProjectCoverImage
                src={project.image}
                alt="Project's image"
                sx={CardMediaStyle}
            />
            {isAuthor && <ProjectStatusChip status={project.status} />}
            <Stack padding="8px 0px">
                <Typography variant="body1" mb={1} sx={TypDescription}>
                    {project.about.description}
                </Typography>
                <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
                    <Typography variant="black-14-medium">
                        Подписчиков: {formatNumber(subscribersCount)}
                    </Typography>
                    <Typography variant="black-14-medium">
                        Участников: {formatNumber(membersCount)}
                    </Typography>
                </Stack>
            </Stack>
            <Stack 
                direction="row"
                justifyContent="space-between" 
                alignItems="center"
                pb={1.5}
            >
                <Stack direction="row" gap={2} >
                    <Button 
                        variant="contained"
                        color="secondary"
                        onClick={handleToggleLike}
                        onPointerDown={handleStopNavigation}
                        disabled={likeState.isLoading || unlikeState.isLoading}
                        sx={{ maxHeight: 32}}
                    >
                        <Stack direction="row" alignItems="center" gap={1} sx={{ lineHeight: 1 }}>
                            {isLiked ? <IconLikeFilled fontSize="small" /> : <IconLike fontSize="small" />}
                            <span onClick={handleOpenLikes} onPointerDown={handleStopNavigation} style={{ userSelect: 'none' }}>
                                {formatNumber(likesCount)}
                            </span>
                        </Stack>
                    </Button>
                    <Button 
                        variant="contained"
                        color="secondary"
                        startIcon={
                            <IconComments />
                        }
                        onClick={handleOpenProjectDiscussion}
                        onPointerDown={handleStopNavigation}
                    >
                        {formatNumber(resolveProjectCommentsTotal(project.counters.comments))}
                    </Button>
                </Stack>
                <ViewCounter count={viewsCount} />
            </Stack>
        </Card>
    )
}
